import { createContext, useState, useRef, useContext, useEffect, createElement, Fragment } from 'react';
import cn from 'classnames';
import styles from 'ui-components/Modal.scss';
import { createPortal } from 'react-dom';

var FOCUSABLE_ELEMENTS = ["a[href]", "area[href]", "input:not([disabled])", "select:not([disabled])", "textarea:not([disabled])", "button:not([disabled])", "[tabindex=\"0\"]"].join(",");
var KEY_TAB = 9;
var KEY_ESC = 27;
var getDocumentBody = function getDocumentBody() {
  var body = typeof window === "object" && window && window.document && window.document.body;
  return body || null;
};
var getFirstAndLastFocusableElement = function getFirstAndLastFocusableElement(element) {
  var nodeList = element.querySelectorAll(FOCUSABLE_ELEMENTS);
  return [nodeList[0], nodeList[nodeList.length - 1]];
};
var trapTab = function trapTab(event, element) {
  var _getFirstAndLastFocus = getFirstAndLastFocusableElement(element),
    firstFocusable = _getFirstAndLastFocus[0],
    lastFocusable = _getFirstAndLastFocus[1];
  if (event.keyCode === KEY_TAB) {
    if (event.shiftKey) {
      if ((document.activeElement === firstFocusable || document.activeElement === element) && lastFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else if (document.activeElement === lastFocusable && firstFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }
};
var ModalElementContext = /*#__PURE__*/createContext(getDocumentBody());
var useModal = function useModal(isOpen, setOpen) {
  var _React$useState = useState(null),
    prevFocused = _React$useState[0],
    setPrevFocused = _React$useState[1];
  var element = useRef(null);
  var rootEl = useContext(ModalElementContext);
  var close = function close() {
    return setOpen(false);
  };
  var open = function open() {
    return setOpen(true);
  };
  var toggle = function toggle() {
    return isOpen ? close() : open();
  };
  var Modal = function Modal(_ref) {
    var className = _ref.className,
      children = _ref.children,
      _ref$noClose = _ref.noClose,
      noClose = _ref$noClose === void 0 ? false : _ref$noClose;
    // Focus correct element
    useEffect(function () {
      if (!rootEl || document.activeElement !== rootEl.querySelector("input")) {
        if (isOpen) {
          setPrevFocused(document.activeElement);
          if (element.current) {
            element.current.focus();
          }
        } else if (prevFocused) {
          prevFocused.focus();
        }
      }
    }, []);
    useEffect(function () {
      var re = rootEl;
      if (!isOpen) {
        return;
      }
      var onScroll = function onScroll(e) {
        e.preventDefault();
      };
      var onKeyDown = function onKeyDown(event) {
        if (element.current) {
          trapTab(event, element.current);
        }
        if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
          return;
        }
        if (noClose) {
          return;
        }
        if (event.which === KEY_ESC) {
          close();
        }
      };
      if (re) {
        re.addEventListener("scroll", onScroll);
        re.addEventListener("keydown", onKeyDown);
        re.style.overflow = "hidden";
        re.style.height = "100%";
      }
      return function () {
        if (re) {
          re.removeEventListener("scroll", onScroll);
          re.removeEventListener("keydown", onKeyDown);
          re.style.overflow = "";
          re.style.height = "";
        }
      };
    }, []);
    if (isOpen && !rootEl) {
      throw new Error("Attempting to render a Modal without a root element");
    }
    return /*#__PURE__*/createElement(Fragment, null, isOpen && rootEl ? /*#__PURE__*/createPortal( /*#__PURE__*/createElement("div", {
      ref: element,
      tabIndex: "-1",
      className: cn(styles.block, className),
      role: "dialog",
      "aria-labelledby": "dialog-title",
      "aria-describedby": "dialog-description"
    }, /*#__PURE__*/createElement("div", {
      className: styles.background,
      onClick: close
    }), children), rootEl) : null);
  };
  return {
    Modal: Modal,
    setOpen: setOpen,
    isOpen: isOpen,
    open: open,
    close: close,
    toggle: toggle
  };
};

export { ModalElementContext, useModal };
//# sourceMappingURL=index.js.map
